import isFinite from 'lodash/isFinite';
import isNumber from 'lodash/isNumber';
import { isEmptyString } from '../predicates/string';
import { isNull, isNullOrUndefined, isUndefined } from '../predicates/type';
import { getDecimalChar } from './culture';
import { isNumber as isNumberFromHelpers } from './formValidators/helpers';
import stringUtils from './string';
import isInteger from 'lodash/isInteger';

const REG_NUMBER_WITH_DOT = /^[+-]?(\d+)([.])?(\d+)?$/;
export const HAS_FLOATING_TRAILING_ZERO_REGEX = /^(\d+[,.]\d*?[1-9]?)0+$/;

export const hasTrailingZeros = (value: string) => {
	return Boolean(value.match(HAS_FLOATING_TRAILING_ZERO_REGEX));
};

/**
 * @param value numeric value with trailing zero(s)
 */
export const removeTrailingZeros = (value: string) => {
	const match = value.match(HAS_FLOATING_TRAILING_ZERO_REGEX);

	return isNull(match) ? value : match[1];
};

export const isValidNumber = (value) => {
	return REG_NUMBER_WITH_DOT.test(value) && isNumberFromHelpers(value);
};

export const isBlankValue = (value) => {
	return stringUtils.isNullOrEmpty(value) || stringUtils.isNullOrWhiteSpace(value);
};

export const getNormalizedDec = (value, { resultType = 'string' } = {}) => {
	const regExp = new RegExp(`[${getDecimalChar()}]`, 'g');
	const normalizedDevValue = String(value).replace(regExp, '.');
	// const normalizedDevValue = String(valueDotUpdated).replace(/[^0-9.-]/, '');

	if (resultType === 'number') {
		return Number(normalizedDevValue);
	}

	return normalizedDevValue;
};

export const padStartZero = (value) => String(value).padStart(2, '0');

export const getLocalDec = (value) => {
	return String(value).replace('.', getDecimalChar());
};

export function isNumeric(value: unknown): boolean {
	if (isNullOrUndefined(value)) {
		return false;
	}

	if (isEmptyString(value)) {
		return false;
	}

	const parsedValue = Number(String(value));

	return isNumber(parsedValue) && isFinite(parsedValue);
}

export function hasNumericNegativeSignOnly(value: unknown): boolean {
	return !!String(value).match(/[-]/) && String(value).length === 1;
}

export function isStrictNumber(value: unknown) {
	return isNumber(value) && isFinite(value);
}

export function toNumber<T = unknown>(value: T, options?: { replaceComma?: boolean }): number {
	const replaceComma = isUndefined(options?.replaceComma) ? true : options?.replaceComma;
	const _value = replaceComma ? String(value).replace(',', '.') : value;

	if (!isNumeric(_value)) {
		return NaN;
	}

	// @ts-ignore
	const parsedNumber = 1 * _value;

	return isStrictNumber(parsedNumber) ? parsedNumber : NaN;
}

export function isPinCode<T = unknown>(value: T): boolean {
	const hasSupportedType = typeof value === 'number' || typeof value === 'string';

	if (!hasSupportedType) {
		return false;
	}

	return !isNull(String(value).match(/^[0-9]*$/));
}

export function isFloat(value: unknown) {
	return Number(value) === value && value % 1 !== 0;
}

export function isInt(value: unknown) {
	return Number(value) === value && value % 1 === 0;
}

export function isMinMaxNumber(
	value: number,
	options: {
		min?: number;
		max?: number;
	},
) {
	if (!isUndefined(options.min) && !isUndefined(options.max)) {
		return value >= options.min && value <= options.max;
	}

	if (!isUndefined(options.min)) {
		return value >= options.min;
	}

	if (!isUndefined(options.max)) {
		return value <= options.max;
	}

	return false;
}

export function formatDecimalScale(originalValue: string | number, decimalScale: number) {
	if (decimalScale === 0) {
		return originalValue;
	}

	let stringedValue = String(originalValue);
	const floatSignMatch = stringedValue.match(/[.,]/);
	let addFloatingSign = false;

	function getZeros(zerosCount: number) {
		return new Array(zerosCount).fill(0).join('');
	}

	let neededZerosCount = 0;

	if (isNull(floatSignMatch)) {
		neededZerosCount = decimalScale;
		addFloatingSign = true;
	} else if (!isUndefined(floatSignMatch.index)) {
		const symbolsAfterFloatingSign = stringedValue.slice(floatSignMatch.index + 1).length;

		if (symbolsAfterFloatingSign < decimalScale) {
			neededZerosCount = decimalScale - symbolsAfterFloatingSign;
		}
	}

	if (neededZerosCount > 0) {
		stringedValue = stringedValue.concat(addFloatingSign ? ',' : '').concat(getZeros(neededZerosCount));
	}

	if (decimalScale !== 0) {
		const floatSignIndex = stringedValue.indexOf(',');

		if (floatSignIndex !== -1) {
			stringedValue = stringedValue.slice(0, floatSignIndex + decimalScale + 1);
		}
	}

	return stringedValue;
}

export const isPositiveInteger = (value: unknown): value is number => {
	return isNumber(value) && isInteger(value) && value > 0;
};
